<template>
    <template v-if="page">
        <section class="radial-grad tw-flex tw-flex-col xl:tw-min-h-dvh xl:tw-justify-evenly">
            <div class="io-container tw-grid tw-gap-8 xl:tw-my-0 xl:tw-grid-cols-2">
                <div>
                    <Tag
                        v-if="page.name"
                        color="primary"
                        :text="page.name" />
                    <h1
                        v-if="page.title"
                        class="tw-mt-6 tw-text-3xl xl:tw-text-5xl"
                        v-html="page.title"></h1>
                </div>
                <div class="tw-flex tw-flex-col tw-items-end tw-justify-end tw-gap-4">
                    <h3
                        v-if="page.subtitle"
                        class="ckeditor tw-mt-auto"
                        v-html="page.subtitle"></h3>
                    <div v-if="page.subtitleButtonText && page.subtitleButtonUrl">
                        <v-btn
                            class="tw-h-12 md:tw-h-14 md:tw-px-7 xl:tw-text-base"
                            color="primary"
                            elevation="0"
                            rounded="pill">
                            <a :href="page.subtitleButtonUrl">{{ page.subtitleButtonText }}</a>
                        </v-btn>
                    </div>
                </div>
            </div>
            <img
                v-if="page.bannerImage"
                alt=""
                class="tw-h-full tw-object-contain max-xl:tw-hidden"
                :src="useMedia(page.bannerImage).original" />
            <img
                v-if="page.bannerImageMobile"
                alt=""
                class="tw-h-80 tw-object-cover xl:tw-hidden"
                :src="useMedia(page.bannerImageMobile).original" />
        </section>
        <section class="io-container radial-grad tw-z-20">
            <h2
                class=""
                v-html="page.experienceTitle"></h2>
            <h3
                class="ckeditor tw-mb-16"
                v-html="page.experienceText"></h3>

            <img
                v-if="page.experienceImage"
                alt=""
                class="tw-h-full tw-object-contain max-xl:tw-max-h-64"
                :src="useMedia(page.experienceImage).original" />
        </section>
        <section class="io-container radial-grad tw-z-20">
            <div class="tw-grid tw-grid-cols-1 xl:tw-grid-cols-12 xl:tw-gap-20">
                <div class="xl:tw-col-span-7">
                    <h2
                        class=""
                        v-html="page.gamificationTitle"></h2>
                    <h3
                        class="ckeditor tw-mb-16"
                        v-html="page.gamificationText"></h3>
                </div>
                <div class="xl:tw-col-span-5">
                    <img
                        v-if="page.gamificationImage"
                        alt=""
                        class="tw-mx-auto tw-h-full tw-object-cover max-xl:tw-max-h-64"
                        :src="useMedia(page.gamificationImage).original" />
                </div>
            </div>
        </section>
        <section class="io-container tw-z-20">
            <h2
                class="tw-text-center"
                v-html="page.areasTitle"></h2>
            <div class="tw-grid tw-gap-5 sm:tw-grid-cols-2 xl:tw-grid-cols-3">
                <div
                    v-for="area in areas"
                    :key="area.id"
                    class="work-area">
                    <h3
                        class="tw-text-xl"
                        v-html="area.name"></h3>
                    <img
                        v-if="area.icon"
                        alt=""
                        class="tw-h-16 tw-w-16 tw-object-contain"
                        :src="useMedia(area.icon).medium" />
                </div>
            </div>
        </section>
        <section
            id="open-positions"
            class="io-container">
            <h2 v-html="page.teamTitle"></h2>
            <h3 v-html="page.teamText"></h3>
            <div class="tw-mt-16 tw-flex tw-gap-4 tw-flex-wrap">
                <div v-if="openPositionsAreas.length > 1" class="tw-flex tw-flex-wrap tw-gap-4">
                    <v-btn
                        v-for="area in openPositionsAreas"
                        :key="area.id"
                        color="blue"
                        elevation="0"
                        rounded="pill"
                        :variant="selectedArea == area.slug ? 'flat' : 'outlined'"
                        @click="selectArea(area.slug)"
                        >{{ area.name }}
                    </v-btn>
                </div>
                <!-- button with "Oportunidades para estudantes" -->
                <v-btn
                    class="tw-ml-auto"
                    color="ioacademy"
                    elevation="0"
                    rounded="pill"
                    :to="{ name: 'ioAcademy' }"
                    variant="flat">
                    {{ $t('career.students') }}
                </v-btn>
            </div>

            <div class="tw-mt-0">
                <div
                    v-if="openPositionsFiltered.length === 0"
                    class="tw-py-12 tw-text-center">
                    <h3
                        v-if="selectedArea !== 'all'"
                        class="tw-text-gray-500 tw-text-xl">
                        {{ $t('career.noPositionsAvailable') }}
                    </h3>
                    <h3
                        v-else
                        class="tw-text-gray-500 tw-text-xl">
                        {{ $t('career.noPositions') }}
                    </h3>
                </div>
                <div
                    v-for="position in openPositionsFiltered"
                    :key="position.id"
                    class="tw-border-b tw-border-b-gray-300 tw-py-8">
                    <h4
                        class="tw-text-lg tw-font-bold 2xl:tw-text-2xl"
                        v-html="position.name"></h4>
                    <p
                        class="tw-mt-2 tw-text-sm tw-font-light xl:tw-max-w-[1050px] 2xl:tw-text-lg"
                        v-html="position.description"></p>
                    <div class="tw-mt-8 tw-flex tw-flex-col tw-items-start tw-gap-4 md:tw-flex-row">
                        <div class="tw-flex tw-items-center tw-gap-2 tw-rounded-full tw-bg-primary tw-px-7 tw-py-2 tw-text-sm tw-text-white">
                            <v-icon icon="mdi-clock-time-eight"></v-icon>
                            <span>{{ position.time }}</span>
                        </div>
                        <div class="tw-flex tw-items-center tw-gap-2 tw-rounded-full tw-bg-primary tw-px-7 tw-py-2 tw-text-sm tw-text-white">
                            <v-icon icon="mdi-map-marker"></v-icon>
                            <span>{{ position.location }}</span>
                        </div>
                        <v-btn
                            class="tw-ml-auto tw-font-semibold xl:tw-text-lg"
                            color="dark"
                            elevation="0"
                            rounded="pill"
                            variant="text"
                            @click="selectPosition(position)"
                            >{{ $t('career.apply') }}
                            <v-icon
                                class="tw-ml-2"
                                icon="mdi-arrow-top-right-thin"></v-icon>
                        </v-btn>
                    </div>
                </div>
            </div>
        </section>
        <section
            id="send-cv"
            class="grad-blue-650">
            <div class="io-container tw-relative tw-z-20 tw-my-0 tw-flex tw-flex-grow tw-flex-col tw-items-start tw-justify-start tw-pb-24 tw-pt-8 tw-text-center 3xl:tw-pt-16">
                <h1
                    v-if="!selectedPosition || isOtherSelected"
                    class="tw-mt-10 tw-text-left tw-font-bold"
                    v-html="page.sendCvTitle"></h1>
                <h1
                    v-if="selectedPosition && !isOtherSelected"
                    class="tw-mt-10 tw-text-left tw-font-bold">
                    {{ $t('career.applyFor') }}:<br />
                    <span
                        class="tw-text-blue"
                        v-html="selectedPosition.name"></span>
                </h1>
                <v-form
                    ref="form"
                    class="tw-mt-14 tw-w-full"
                    @submit.prevent="onFormSubmit">
                    <div class="tw-grid tw-grid-cols-1 tw-gap-x-8 tw-gap-y-6 md:tw-grid-cols-2 lg:tw-gap-x-16">
                        <div class="tw-grow tw-text-left">
                            <v-label class="tw-mb-3 tw-text-lg tw-text-dark tw-opacity-100">{{ $t('career.name') }}*</v-label>
                            <v-text-field
                                v-model.trim="name"
                                flat
                                name="name"
                                :placeholder="$t('career.namePlaceholder')"
                                :rules="[$rules.required]"
                                variant="solo"></v-text-field>
                        </div>
                        <div class="tw-grow tw-text-left">
                            <v-label class="tw-mb-3 tw-text-lg tw-text-dark tw-opacity-100">{{ $t('career.email') }}*</v-label>
                            <v-text-field
                                v-model.trim="email"
                                flat
                                name="email"
                                :placeholder="$t('career.emailPlaceholder')"
                                :rules="[$rules.required, $rules.email]"
                                type="email"
                                variant="solo"></v-text-field>
                        </div>
                        <div class="tw-grow tw-text-left">
                            <v-label class="tw-mb-3 tw-text-lg tw-text-dark tw-opacity-100">{{ $t('career.phone') }}</v-label>
                            <v-text-field
                                v-model.trim="phone"
                                flat
                                name="phone"
                                :placeholder="$t('career.phonePlaceholder')"
                                type="tel"
                                variant="solo"></v-text-field>
                        </div>
                        <div class="tw-grow tw-text-left">
                            <v-label class="tw-mb-3 tw-text-lg tw-text-dark tw-opacity-100">{{ $t('career.position') }}</v-label>
                            <!-- v-select with open positions -->
                            <v-select
                                v-model="selectedPosition"
                                clearable
                                density="comfortable"
                                elevation="0"
                                flat
                                hide-details="auto"
                                item-title="name"
                                item-value="documentId"
                                :items="openPositionsSelect"
                                return-object
                                rounded="pill"
                                variant="solo"></v-select>
                        </div>
                    </div>
                    <div class="tw-mt-6 tw-w-full tw-text-left">
                        <v-label class="tw-mb-3 tw-text-lg tw-text-dark tw-opacity-100">{{ $t('career.cv') }}</v-label>
                        <template v-if="!cv">
                            <v-file-upload
                                v-model="cv"
                                border="0"
                                class="tw-rounded-3xl"
                                clearable
                                color="white"
                                density="compact"
                                @update:model-value="onCvSet">
                                <template #icon>
                                    <v-icon
                                        color="blue-55"
                                        icon="mdi-file-document"></v-icon>
                                </template>
                                <template #title>
                                    <p class="tw-text-xs tw-font-light tw-text-gray-700" v-html="$t('career.cvInstructions')"></p>
                                </template>
                            </v-file-upload>
                            <p
                                v-if="fileTooBig"
                                class="tw-mt-2 tw-text-xs tw-font-normal tw-text-red">
                                {{ $t('career.fileTooBig') }}
                            </p>
                        </template>
                        <div
                            v-else
                            class="tw-flex tw-w-full tw-items-center tw-justify-center tw-rounded-3xl tw-bg-white tw-bg-opacity-50 tw-p-4">
                            <span class="tw-text-xs tw-font-light tw-text-gray-700">{{ $t('career.selectedFile') }}: {{ cv?.name }}</span>
                            <v-btn
                                class="tw-text-lg tw-font-semibold"
                                color="dark"
                                elevation="0"
                                rounded="pill"
                                variant="text"
                                @click="cv = undefined">
                                <v-icon
                                    class="tw-ml-2"
                                    color="blue"
                                    icon="mdi-delete"></v-icon>
                            </v-btn>
                        </div>
                    </div>
                    <div class="tw-mt-6 tw-w-full tw-text-left">
                        <v-label class="tw-mb-3 tw-text-lg tw-text-dark tw-opacity-100">{{ $t('career.message') }}*</v-label>
                        <v-textarea
                            v-model.trim="message"
                            class="message-area"
                            flat
                            hide-details="auto"
                            :placeholder="$t('career.messagePlaceholder')"
                            :rules="[$rules.required]"
                            variant="solo"></v-textarea>
                    </div>
                    <div class="tw-mt-8 tw-text-right">
                        <p
                            v-if="response"
                            class="tw-mb-3 tw-text-lg"
                            :class="responseType === 'success' ? 'tw-text-green' : 'tw-text-red'">
                            {{ response }}
                        </p>
                        <v-btn
                            v-if="!response || (response && responseType === 'error')"
                            class="tw-px-8"
                            color="primary"
                            elevation="0"
                            height="50"
                            :loading="isLoading"
                            rounded="pill"
                            type="submit">
                            {{ $t('career.send') }}
                        </v-btn>
                    </div>
                </v-form>
            </div>
        </section>
    </template>
    <template v-else>
        <!-- loader -->
        <Loader />
    </template>
</template>

<script setup lang="ts">
    import { computed, ref } from 'vue';
    import { useMedia } from '@/composables/useMedia';
    import { useAppStore } from '@/store/app';
    import Loader from '@/components/Loader.vue';
    import useRules from '@/composables/rules';
    import { createCVContact } from '@/api/contacts';
    import { uploadFiles } from '@/api/upload';
    import _ from 'lodash';
    import { useI18n } from 'vue-i18n';

    const { t } = useI18n();
    const appStore = useAppStore();
    const $rules = useRules();

    const name = ref('');
    const email = ref('');
    const phone = ref('');
    const cv = ref<File | File[] | undefined>(undefined);
    const fileTooBig = ref(false);
    const message = ref('');
    const response = ref('');
    const responseType = ref<'success' | 'error'>('error');
    const isLoading = ref(false);
    const form = ref();
    const selectedPosition = ref(null);

    const selectedArea = ref<string>('all');

    const page = computed(() => {
        return appStore.getCareers;
    });

    const areas = computed(() => {
        return appStore.getWorkAreas;
    });

    const openPositions = computed(() => {
        return appStore.getOpenPositions;
    });

    const openPositionsAreas = computed(() => {
        // Extract areas from positions
        const areas = openPositions.value.map((position: any) => position.area);

        // Create a map to store unique areas by slug
        const uniqueAreasMap = new Map();

        // Add each area to the map, using slug as the key
        areas.forEach((area: any) => {
            uniqueAreasMap.set(area.slug, area);
        });

        // Convert map values back to array
        const uniqueAreas = Array.from(uniqueAreasMap.values());

        // Sort areas alphabetically by name
        uniqueAreas.sort((a: any, b: any) => a.name.localeCompare(b.name));

        // Add the 'All' option at the beginning
        uniqueAreas.unshift({ name: 'Todas', slug: 'all' });

        return uniqueAreas;
    });

    const openPositionsFiltered = computed(() => {
        if (selectedArea.value === 'all') {
            return openPositions.value;
        } else {
            return openPositions.value.filter((position: any) => position.area.slug === selectedArea.value);
        }
    });

    const openPositionsSelect = computed(() => {
        const pos = _.cloneDeep(openPositions.value);
        pos.push({
            name: t('career.otherPosition'),
            documentId: 'other',
        });
        return pos;
    });

    const isOtherSelected = computed(() => {
        return selectedPosition.value?.documentId === 'other';
    });

    function onCvSet(files: File | File[]) {
        if (isFileTooBig(files)) {
            fileTooBig.value = true;
            cv.value = undefined;
            return;
        }
        fileTooBig.value = false;
        cv.value = files;
    }

    function isFileTooBig(file: File | File[] | undefined) {
        if (!file) return false;
        return Array.isArray(file) ? file.some((f) => f.size > 10 * 1024 * 1024) : file.size > 10 * 1024 * 1024;
    }

    function selectArea(slug: string) {
        selectedArea.value = slug;
    }

    function selectPosition(position: any) {
        selectedPosition.value = position;
        // scroll to send-cv section
        document.getElementById('send-cv')?.scrollIntoView({ behavior: 'smooth' });
    }

    async function onFormSubmit() {
        if (!form.value) return;
        form.value.resetValidation();
        response.value = '';
        const { valid } = await form.value.validate();
        if (!valid) return;
        isLoading.value = true;
        try {
            const { data } = await uploadFiles({
                files: cv.value as File,
            });

            await createCVContact({
                name: name.value,
                email: email.value,
                phone: phone.value,
                message: message.value,
                file: data[0].id,
                open_position: isOtherSelected.value || !selectedPosition.value ? undefined : selectedPosition.value.documentId,
            });
            response.value = t('career.successMessage');
            responseType.value = 'success';
            cv.value = undefined;
            selectedPosition.value = null;
            form.value.reset();
        } catch (error) {
            console.error(error);
            response.value = t('career.errorMessage');
            responseType.value = 'error';
        } finally {
            isLoading.value = false;
        }
    }

    watchEffect(() => {
        if (page.value?.name) {
            document.title = page.value?.name + ' - IOTech';
        }
    });
</script>

<style scoped lang="scss">
    .work-area {
        @apply tw-flex tw-items-center tw-justify-between tw-gap-6 tw-rounded-3xl tw-px-10 tw-py-8 tw-font-semibold tw-text-white;

        background: url('@/assets/gradients/gradient-work-area.png');
        background-position: center center;
        background-size: cover;
        background-repeat: no-repeat;
    }
</style>
<style lang="scss">
    .v-form .v-field,
    div.v-sheet {
        @apply tw-bg-white tw-bg-opacity-50 #{!important};
    }

    .message-area .v-field {
        @apply tw-rounded-3xl #{!important};
    }
</style>
